export type HighlightElements = {
  list: HTMLElement
  search: HTMLElement | null
}

export type Highlight = {
  move(delta: number): void
  pick(): void
  reset(): void
}

export const createHighlight = (
  elements: HighlightElements,
  onActivate: (row: HTMLElement) => void
): Highlight => {
  let activeIndex = -1

  const options = (): HTMLElement[] =>
    Array.from(elements.list.querySelectorAll<HTMLElement>('[role="option"]'))

  const clearActiveDescendant = (): void => {
    elements.search?.removeAttribute('aria-activedescendant')
  }

  const paint = (rows: HTMLElement[]): void => {
    rows.forEach((row, index) => {
      const active = index === activeIndex

      row.setAttribute('aria-selected', active ? 'true' : 'false')
      row.classList.toggle('is-active', active)
    })
  }

  const move = (delta: number): void => {
    const rows = options()
    if (rows.length === 0) return

    activeIndex = (activeIndex + delta + rows.length) % rows.length
    paint(rows)

    elements.search?.setAttribute('aria-activedescendant', rows[activeIndex].id)
    rows[activeIndex].scrollIntoView({ block: 'nearest' })
  }

  const pick = (): void => {
    const rows = options()
    const row = rows[activeIndex] ?? rows[0]
    if (!row) return

    if (row.dataset.type === 'nested') {
      row.querySelector<HTMLElement>('[popovertarget]')?.click()

      return
    }

    const actionable = row.querySelector<HTMLElement>('a, button')
    if (actionable) {
      actionable.click()

      return
    }

    onActivate(row)
  }

  const reset = (): void => {
    activeIndex = -1
    clearActiveDescendant()
  }

  return { move, pick, reset }
}
